export type PushState =
  | 'unsupported'
  | 'ios-not-installed'
  | 'denied'
  | 'not-subscribed'
  | 'subscribed';

export interface PushEnv {
  hasPushApi: boolean;
  isIos: boolean;
  isStandalone: boolean;
  permission: NotificationPermission;
  /** Адрес подписки, которая есть в этом браузере, если есть. */
  browserEndpoint: string | null;
  /** Адреса устройств, которые сервер числит за текущим человеком. */
  myEndpoints: string[];
}

/**
 * Состояние раздела «Уведомления» на этом устройстве. Порядок важен: у Safari во
 * вкладке нет Push API, и ему нужна подсказка про установку, а не «не поддерживается».
 */
export function pushState(e: PushEnv): PushState {
  if (e.isIos && !e.isStandalone) return 'ios-not-installed';
  if (!e.hasPushApi) return 'unsupported';
  if (e.permission === 'denied') return 'denied';
  // Подписка есть в браузере, но числится за другим человеком (общий компьютер) —
  // для текущего пользователя устройство не подписано.
  if (e.browserEndpoint && e.myEndpoints.includes(e.browserEndpoint)) return 'subscribed';
  return 'not-subscribed';
}

const hasPushApi = () =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export async function browserSubscription(): Promise<PushSubscription | null> {
  if (!hasPushApi()) return null;
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

export async function readPushEnv(myEndpoints: string[]): Promise<PushEnv> {
  const ua = navigator.userAgent;
  const subscription = await browserSubscription();
  return {
    hasPushApi: hasPushApi(),
    // iPadOS представляется как Mac, но с сенсорным экраном.
    isIos: /iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && navigator.maxTouchPoints > 1),
    isStandalone:
      window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true,
    permission: 'Notification' in window ? Notification.permission : 'default',
    browserEndpoint: subscription?.endpoint ?? null,
    myEndpoints,
  };
}

/** Публичный ключ VAPID приходит в base64url, а `subscribe` ждёт байты. */
export function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const raw = atob(padded);
  // Явно на ArrayBuffer: subscribe() не принимает массив поверх SharedArrayBuffer.
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

/** «Chrome, macOS» — чтобы в списке устройств было понятно, что есть что. */
export function deviceLabel(): string {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Firefox\//.test(ua)
      ? 'Firefox'
      : /Chrome\//.test(ua)
        ? 'Chrome'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'Browser';
  const os = /Windows/.test(ua)
    ? 'Windows'
    : /Android/.test(ua)
      ? 'Android'
      : /iPhone|iPad/.test(ua)
        ? 'iOS'
        : /Mac/.test(ua)
          ? 'macOS'
          : /Linux/.test(ua)
            ? 'Linux'
            : '';
  return os ? `${browser}, ${os}` : browser;
}
